import { Chip, ChipGroup } from '@src/components/common/ChipGroup';
import useAddMediaToTimeline from '@src/hooks/useAddMediaToTimeline';
import { useEditor } from '@src/stores-v2';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { forIPad } from '@src/utility/styled';
import _uniqBy from 'lodash/uniqBy';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import BackIcon from '../../../svgs/BackIcon';
import { checkWordsInString } from '../../../utility/general';
import SearchInput from '../SearchInput';
import SongCollection from '../SongCollection';
import AudioLibrary from './AudioLibrary';
import MusicSongItem from './MusicSongItem';
import MusicTheme from './MusicTheme';

import type { Music, Song } from '../../../types.ts/story';
import type { AddSongToTrackOptions } from '../SongCollection';
import type { FC } from 'react';

type SongWithGenre = (Music['collection'][0] & { genre: string })[];

type MusicTypes = 'stock-music' | 'my-audio' | 'org-audio' | 'sound-effect';

const MUSIC_SECTION_WIDTH_PX = 300;

const MusicLibrary: FC<{}> = observer(() => {
  const videoCreator = useVideoCreatorStore();
  const editorStore = useEditor();

  const stockMusic = videoCreator.stockMusic as Music[];
  const { addMediaToTimeline } = useAddMediaToTimeline({
    editorStore,
    videoCreator,
  });

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [currentSong, setCurrentSong] = useState('');
  const [selectedTab, setSelectedTab] = useState<MusicTypes>('stock-music');
  const [searchValues, setSearchValues] = useState<SongWithGenre>();

  const [selectedGenre, setSelectedGenre] = useState<string>('');

  if (!stockMusic && !videoCreator.story?.myAudios) {
    return <div>You don't have any music themes.</div>;
  }

  const preloadAudio = (url: string) => {
    const audio = new Audio(url);
    audio.preload = 'auto';
    audio.load();
  };

  useEffect(() => {
    if (!selectedGenre) return;
    const selectedCollection = stockMusic?.find(
      (m: Music) => m.genre === selectedGenre,
    );
    if (!selectedCollection) return;

    for (const song of selectedCollection.collection) {
      preloadAudio(song.url);
      if (!videoCreator.audioTracksData[song.url]) {
        videoCreator.loadWaveformForSource({
          source: song.url,
          name: song.title,
        });
      }
    }
  }, [selectedGenre]);

  useEffect(() => {
    if (!searchValues) return;

    for (const song of searchValues) {
      if (!videoCreator.audioTracksData[song.url]) {
        videoCreator.loadWaveformForSource({
          source: song.url,
          name: song.title,
        });
      }
    }
  }, [searchValues]);

  const handleSearchMusic = (text: string) => {
    const collections = stockMusic
      ?.flatMap((m: Music) =>
        m.collection.map((coll: Song) => ({ ...coll, genre: m.genre })),
      )
      .filter((s: Song & { genre: string }) => {
        if (selectedGenre) return s.genre === selectedGenre;
        return true;
      }) as SongWithGenre;

    if (!text) return setSearchValues(collections);

    const matched =
      collections?.filter(c => {
        const checkSongName =
          c.customData?.songName &&
          checkWordsInString(c.customData?.songName, text);

        const checkArtist =
          c.customData?.artist &&
          checkWordsInString(c.customData?.artist, text);

        const checkTags = c.tags?.some(t => checkWordsInString(t || '', text));

        return (
          checkSongName ||
          checkArtist ||
          c.tags?.includes(text.toLowerCase()) ||
          (text?.length > 3 && checkTags)
        );
      }) || [];

    const uniqueMatched = _uniqBy(matched, 'url');
    setSearchValues(uniqueMatched);
  };

  const handleAddToTrack = async (
    song: Song,
    options: AddSongToTrackOptions,
  ) => {
    const songDuration = parseFloat(song.customData?.duration);
    const songName = song.customData?.songName?.replace(/\b\w/g, match =>
      match.toUpperCase(),
    );
    addMediaToTimeline(
      {
        id: song.id,
        description: songName,
        src: song.url,
        title: songName,
        duration: songDuration,
      },
      'audio',
      {
        fitToEnd: options?.fitSongToVideo || false,
      },
    );
  };

  const renderBackButton = () => (
    <>
      {selectedGenre || searchValues ? (
        <Back
          onClick={() => {
            setSelectedGenre('');
            setSearchValues(undefined);
          }}
        >
          <BackIcon />
        </Back>
      ) : null}
    </>
  );

  const renderThemes = () => (
    <>
      {!selectedGenre && !searchValues ? (
        <>
          {selectedTab === 'stock-music' ? (
            <MusicTheme
              music={stockMusic}
              setSelectedGenre={setSelectedGenre}
            />
          ) : null}
        </>
      ) : null}
    </>
  );

  const renderTabs = () => (
    <Tabs>
      <Tab
        isSelected={selectedTab === 'stock-music'}
        onClick={() => setSelectedTab('stock-music')}
      >
        Stock
      </Tab>

      <Tab
        isSelected={selectedTab === 'my-audio'}
        onClick={() => setSelectedTab('my-audio')}
      >
        Story
      </Tab>
      <Tab
        isSelected={selectedTab === 'org-audio'}
        onClick={() => setSelectedTab('org-audio')}
      >
        Organization
      </Tab>
      {/* <Tab
        isSelected={selectedTab === 'sound-effect'}
        onClick={() => setSelectedTab('sound-effect')}
      >
        Effects
      </Tab> */}
    </Tabs>
  );

  const renderCollectionByGenre = (music: Music) => (
    <Collection key={music.id}>
      {selectedGenre === music.genre && (
        <Songs>
          {!music.collection.length ? (
            <div className="no-content">You have no song in this theme</div>
          ) : (
            <SongCollection onAddToTrack={handleAddToTrack}>
              {({ handleSongSelect, isSongSelected }) =>
                music.collection?.map(song => (
                  <MusicSongItem
                    key={song.id}
                    audioRef={audioRef}
                    song={song}
                    currentSong={currentSong}
                    setCurrentSong={setCurrentSong}
                    mood={selectedGenre}
                    onSongClick={handleSongSelect}
                    customTimelineScale={
                      (MUSIC_SECTION_WIDTH_PX /
                        parseFloat(song.customData.duration)) *
                      1
                    }
                    isHighlighted={isSongSelected(song)}
                  />
                ))
              }
            </SongCollection>
          )}
        </Songs>
      )}
    </Collection>
  );

  const renderNoSearchContent = () => (
    <Collection>
      <Songs>
        <div className="no-content">No matching search result.</div>
      </Songs>
    </Collection>
  );

  const renderSearchResults = (
    searchValues: (Music['collection'][0] & { genre: string })[],
  ) => (
    <Collection>
      <Songs>
        <SongCollection onAddToTrack={handleAddToTrack}>
          {({ handleSongSelect, isSongSelected }) =>
            searchValues.map(song => (
              <MusicSongItem
                key={song.id}
                audioRef={audioRef}
                song={song}
                currentSong={currentSong}
                setCurrentSong={setCurrentSong}
                mood={song.genre}
                onSongClick={handleSongSelect}
                customTimelineScale={
                  (MUSIC_SECTION_WIDTH_PX /
                    parseFloat(song.customData.duration)) *
                  1
                }
                isHighlighted={isSongSelected(song)}
              />
            ))
          }
        </SongCollection>
      </Songs>
    </Collection>
  );

  return (
    <Library>
      <audio ref={audioRef} preload="auto">
        <source src="" />
      </audio>

      {renderBackButton()}
      {renderTabs()}

      {selectedTab === 'stock-music' && (
        <SearchInput
          placeholder="Search music"
          handleAction={handleSearchMusic}
          radius="var(--radius-input)"
        />
      )}
      {renderThemes()}

      {selectedTab === 'stock-music' ? (
        <MusicCollection>
          {(() => {
            if (searchValues?.length) {
              return renderSearchResults(searchValues);
            } else if (searchValues?.length === 0) {
              return renderNoSearchContent();
            } else if (selectedGenre) {
              return stockMusic.map(music => renderCollectionByGenre(music));
            } else {
              return null;
            }
          })()}
        </MusicCollection>
      ) : null}

      {selectedTab === 'my-audio' && (
        <AudioLibrary
          type={selectedTab}
          currentSong={currentSong}
          audioRef={audioRef}
          handleAddToTrack={handleAddToTrack}
          setCurrentSong={setCurrentSong}
          preloadAudio={preloadAudio}
        />
      )}
      {selectedTab === 'org-audio' && (
        <AudioLibrary
          type={selectedTab}
          currentSong={currentSong}
          audioRef={audioRef}
          handleAddToTrack={handleAddToTrack}
          setCurrentSong={setCurrentSong}
          preloadAudio={preloadAudio}
        />
      )}
    </Library>
  );
});

export default MusicLibrary;

const Back = styled.button`
  outline: 0;
  border: 0;
  cursor: pointer;
  background-color: var(--background);
  width: 0;
  margin-top: 15px;
`;

const Tab = styled(Chip)`
  ${forIPad(`
    padding: 3px 8px;
  `)}
`;

const Tabs = styled(ChipGroup)``;

const Library = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
  margin-top: 5px;
  height: 100%;
  overflow-y: auto;
  scrollbar-gutter: stable both-edges;
  padding-left: var(--spacing-sm);
  padding-right: var(--spacing-sm);
  /* No bottom spacing here: the panel card's Body already contributes
     --spacing-md, which is the whole of what the Speakers panel uses and is the
     pattern every tab now follows. Adding more here double-counted it. */
`;

const MusicCollection = styled.div`
  margin-top: var(--spacing-md);
`;

const Collection = styled.div`
  display: flex;
  align-items: flex-start;
`;

const Songs = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--spacing-sm);
  text-transform: capitalize;
  .no-content {
    text-transform: none;
  }
`;
